import { useEffect, useMemo, useState } from 'react';

import { filterExpressComponents } from '../../utils';
import { PaymentMethod } from '../../../../../../contracts';
import { useCheckout } from '../../../../../../hooks/use-checkout';
import { usePaymentState } from '../../../../hooks/use-payment-state';
import { logPerformance } from '../../../../../../utils/performance-utils';

type ExpressComponentLoadingState = { isLoading: boolean; isAvailable: boolean };
type ExpressComponentLoadingStates = Record<string, ExpressComponentLoadingState>;

export function useExpressComponents(
  onComponentsReady: (isComponentsAvailable: boolean) => void,
  paymentMethods?: PaymentMethod[]
) {
  const { checkoutData } = useCheckout();
  const checkoutConfigurations = checkoutData?.checkoutConfigurations;

  const expressComponents = useMemo(
    () => filterExpressComponents(paymentMethods, checkoutConfigurations),
    [paymentMethods, checkoutConfigurations]
  );

  const [loadingStates, setLoadingStates] = useState<ExpressComponentLoadingStates>(() =>
    expressComponents.reduce(
      (acc, { key }) => ({
        ...acc,
        [key]: {
          isLoading: true,
          isAvailable: false,
        },
      }),
      {}
    )
  );

  const setIsExpressCheckoutElementLoading = usePaymentState(
    (state) => state.setIsExpressCheckoutElementLoading
  );

  useEffect(() => {
    const isAllComponentsLoaded = Object.values(loadingStates).every((value) => !value.isLoading);
    if (isAllComponentsLoaded) {
      logPerformance('Express Components');
      setIsExpressCheckoutElementLoading(false);
      const isComponentsAvailable = Object.values(loadingStates).some((value) => value.isAvailable);
      onComponentsReady(isComponentsAvailable);
    }
  }, [loadingStates, setIsExpressCheckoutElementLoading, onComponentsReady]);

  useEffect(() => {
    setLoadingStates((prev) =>
      expressComponents.reduce((acc, { key }) => ({ ...acc, [key]: prev[key] }), {})
    );
  }, [expressComponents]);

  const handleComponentReady = (key: string, isAvailable: boolean) => {
    setLoadingStates((prev) => ({ ...prev, [key]: { isLoading: false, isAvailable } }));
  };

  return {
    expressComponents,
    handleComponentReady,
  };
}
